import styled from '@emotion/styled'
import { TASK_MESSAGE_TYPE_EMAIL } from 'Api/beAutomationApi'
import MessageSelect from 'Components/MessageSelect/MessageSelect'
import ModalSmsSender from 'Components/ModalSmsSender/ModalSmsSender'
import SmsSelect from 'Components/SmsSelect/SmsSelect'
import {
  trustedSmsSenderState,
  useTrustedSmsSender,
  useTrustedSmsSenderCode
} from 'Hooks/useTrustedSmsSenderTask'
import { STORE_KEY_ACCOUNT, notifyParent, storeGet } from 'mailup-react-helpers'
import {
  ChipIconOpenNew,
  Flex,
  FlexItem,
  FormElement,
  GRID_VARIANT_VERTICAL_BIG,
  Grid,
  GridCell,
  HelpIcon,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  MessageBox,
  Panel,
  SPACING_24,
  SPACING_8,
  SpacingVertical,
  TEXT_VARIANT_LIGHT,
  TITLE_VARIANT_M,
  Text,
  Title,
  Toggle,
  TooltipUtils,
  TrustedSmsBoxCode,
  TrustedSmsInput
} from 'mailup-web-components'
import {
  copyMessageCallback,
  copyMessageSmsCallback,
  editMessageDetailsByIdCallback,
  errorCallback,
  getEmailSelectedMessageCallback,
  getSmsSelectedMessageCallback
} from 'Pages/Task/recoil/callbacks/callbacks'
import {
  modalSmsSenderState,
  modalTrustedSmsSenderCodeRequestState,
  modalUsedMessageState
} from 'Pages/Task/recoil/state/modals'
import {
  ACTION_MOVE_COPY,
  ACTION_SEND_MESSAGE,
  ACTION_SEND_SMS,
  senderConfigState,
  taskState
} from 'Pages/Task/recoil/state/task'
import { useEffect } from 'react'

import { useIntl } from 'react-intl'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil'
import DynamicSenderEmail from './DynamicSenderEmail'
import DynamicSenderName from './DynamicSenderName'
import InputSenderReplyTo from './InputReplyTo'
import InputSenderEmail from './InputSenderEmail'
import InputSenderName from './InputSenderName'

import { ModalStepMessageUsed } from './ModalStepMessageUsed'
import { ModalTrustedSmsSenderCodeRequest } from './ModalTrustedSmsSenderCodeRequest'

const StyledHelpIcon = styled(HelpIcon)`
  svg {
    top: 2px !important;
  }
`

const IconContainer = styled.span`
  text-decoration: none !important;
  margin-left: 4px;
  > svg {
    width: 12px;
    height: 12px;
  }
`

export const SendSettings = () => {
  const { Detail } = useRecoilValue(taskState)
  const intl = useIntl()
  const {
    IsPecPlus,
    CustomSender,
    SenderNameSMS,
    IdMessage,
    idMessageIsDirty
  } = useRecoilValue(senderConfigState)
  const setSenderConfig = useSetRecoilState(senderConfigState)
  const editMessageDetailsById = useRecoilCallback(
    editMessageDetailsByIdCallback,
    []
  )
  const itemKeyInputSender = 'taskEditSmsSender'
  const { onVerifyClick, ...trustedSmsSenderProps } = useTrustedSmsSender(
    itemKeyInputSender,
    true,
    SenderNameSMS
  )
  const onError = useRecoilCallback(errorCallback, [])
  const trustedSmsSenderCodeProps = useTrustedSmsSenderCode(
    'taskEditSmsSenderCode',
    itemKeyInputSender
  )

  const { trustedSmsBoxCodeVisible } = useRecoilValue(
    trustedSmsSenderState('taskEditSmsSender')
  )

  const [
    { show: showModalTrustedSmsSenderCodeRequest },
    setModalTrustedSmsSenderCodeRequest
  ] = useRecoilState(modalTrustedSmsSenderCodeRequestState)

  const handleVerify = () => {
    setModalTrustedSmsSenderCodeRequest((prev) => ({
      ...prev,
      show: true
    }))
  }

  const handleModalTrustedSmsSenderCodeRequestCancel = () => {
    setModalTrustedSmsSenderCodeRequest((prev) => ({
      ...prev,
      show: false
    }))
  }
  const handleModalTrustedSmsSenderCodeRequestConfirm = () => {
    onVerifyClick()
    setModalTrustedSmsSenderCodeRequest((prev) => ({
      ...prev,
      show: false
    }))
  }

  const getEmailSelectedMessage = useRecoilCallback(
    getEmailSelectedMessageCallback,
    []
  )
  const getSmsSelectedMessage = useRecoilCallback(
    getSmsSelectedMessageCallback,
    []
  )

  // Modal SmsSender
  const [{ show: showModalSmsSender }, setModalSmsSender] =
    useRecoilState(modalSmsSenderState)
  const handleModalSmsSenderCancel = () => {
    setModalSmsSender((prev) => ({
      ...prev,
      show: false
    }))
  }
  const handleModalSmsSenderConfirm = (event, value) => {
    setModalSmsSender((prev) => ({
      ...prev,
      show: false,
      alias: value.Alias
    }))
  }

  useEffect(() => {
    if (Detail.Action === ACTION_SEND_SMS) {
      setSenderConfig((prev) => ({
        ...prev,
        IsDirty: true,
        SenderNameSMS: trustedSmsSenderProps.value
      }))
    }
  }, [trustedSmsSenderProps.value])

  const handleChange = () => {
    setSenderConfig((prev) => ({
      ...prev,
      IsDirty: true,
      CustomSender: !prev.CustomSender
    }))
  }

  useEffect(() => {
    setSenderConfig((prev) => ({
      ...prev,
      sernderSmsValid:
        trustedSmsSenderProps.variant === 'success' ||
        trustedSmsSenderProps.variant === 'neutral'
    }))
  }, [trustedSmsSenderProps.variant])

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [Detail.Action])

  const handlePreview = () => {
    notifyParent('openPreviewMainFrame', [IdMessage, '&source=newtab'])
  }
  const handleEdit = () => {
    if (Detail.Action === ACTION_SEND_MESSAGE && IdMessage > 0) {
      editMessageDetailsById(IdMessage)
    }
    if (Detail.Action === ACTION_SEND_SMS && IdMessage > 0) {
      const { Domain } = storeGet(STORE_KEY_ACCOUNT)
      // notifyParent('openNewTab', [
      //   `https://${Domain}/console/form_upd_SMS.aspx?source=smslist&idNL=${IdMessage}`
      // ])
      notifyParent('openNewTab', [
        `https://${Domain}/console/form_upd_SMS.aspx?idNL=${IdMessage}`
      ])
    }
  }

  const LabelRightComponent = () => {
    const intl = useIntl()

    return (
      <div>
        {Detail.Action === ACTION_SEND_MESSAGE && (
          <>
            <Link
              variant={LINK_VARIANT_EXTRALIGHT}
              onClick={handlePreview}
              disabled={!IdMessage}
            >
              {intl.formatMessage({
                id: 'Load.MailupCommon.Preview'
              })}
            </Link>
            <Text variant={TEXT_VARIANT_LIGHT} htmlElement='span'>
              {' '}
              |{' '}
            </Text>
          </>
        )}
        <Link
          variant={LINK_VARIANT_EXTRALIGHT}
          onClick={handleEdit}
          disabled={!IdMessage}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Edit' })}
          <IconContainer>
            <ChipIconOpenNew />
          </IconContainer>
        </Link>
      </div>
    )
  }

  // entity

  const [{ show: showUsed, entityName, type }, setModalUsedMessageState] =
    useRecoilState(modalUsedMessageState)

  const copyMessageEmail = useRecoilCallback(copyMessageCallback, [])
  const copyMessageSms = useRecoilCallback(copyMessageSmsCallback, [])

  const handleChangeSelect = async (value) => {
    if (Detail.Action === ACTION_SEND_MESSAGE) {
      await getEmailSelectedMessage(value)
    } else {
      await getSmsSelectedMessage(value)
    }
  }

  const handleMessageUsedConfirm = async () => {
    setModalUsedMessageState((prev) => ({
      ...prev,
      show: false
    }))

    if (type === TASK_MESSAGE_TYPE_EMAIL) {
      await copyMessageEmail()
    } else {
      await copyMessageSms()
    }
  }

  return (
    <>
      <ModalTrustedSmsSenderCodeRequest
        show={showModalTrustedSmsSenderCodeRequest}
        number={trustedSmsSenderProps.value}
        handleCancel={handleModalTrustedSmsSenderCodeRequestCancel}
        handleConfirm={handleModalTrustedSmsSenderCodeRequestConfirm}
      />
      <ModalSmsSender
        show={showModalSmsSender}
        handleCancel={handleModalSmsSenderCancel}
        handleConfirm={handleModalSmsSenderConfirm}
      />
      <ModalStepMessageUsed
        show={showUsed}
        handleConfirm={handleMessageUsedConfirm}
        name={entityName}
      />
      <Panel>
        <Title variant={TITLE_VARIANT_M}>
          {intl.formatMessage({ id: 'Load.Task.ActionsEmailPanelTitle' })}
        </Title>
        <SpacingVertical />
        <FormElement
          label={intl.formatMessage({
            id: 'Load.Task.ActionsEmailMsgSelectLabel'
          })}
          required
          LabelRightComponent={
            Detail.Action !== ACTION_MOVE_COPY ? LabelRightComponent : null
          }
          feedback={
            idMessageIsDirty && !IdMessage
              ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
              : null
          }
        >
          {Detail.Action === ACTION_SEND_MESSAGE && (
            <MessageSelect
              onErrorCallback={onError}
              IdMessage={IdMessage}
              onChangeCallback={handleChangeSelect}
              error={idMessageIsDirty && !IdMessage}
            />
          )}

          {Detail.Action === ACTION_SEND_SMS && (
            <SmsSelect
              onErrorCallback={onError}
              IdMessage={IdMessage}
              onChangeCallback={handleChangeSelect}
              error={idMessageIsDirty && !IdMessage}
            />
          )}
        </FormElement>
        <SpacingVertical variant={SPACING_24} />
        {Detail.Action === ACTION_SEND_MESSAGE && (
          <>
            <Grid cols={2}>
              <GridCell>
                <InputSenderEmail />
              </GridCell>
              <GridCell>
                <InputSenderName />
              </GridCell>
            </Grid>
            <Grid cols={2} variantVertical={GRID_VARIANT_VERTICAL_BIG}>
              {!IsPecPlus && (
                <>
                  <GridCell span={2}>
                    <SpacingVertical variant={SPACING_8} />
                    <Flex>
                      <Toggle
                        checked={CustomSender}
                        onChange={handleChange}
                        variant='sm'
                      />
                      <FlexItem margin='0 0 0 8px'>
                        <Link onClick={handleChange}>
                          {intl.formatMessage({
                            id: 'Load.EmailSendingWizard.ChangeSenderName'
                          })}
                        </Link>
                      </FlexItem>
                      <TooltipUtils
                        id='customSenderTooltip'
                        type='light'
                        effect='solid'
                        className='c-tooltip'
                      >
                        {intl.formatMessage(
                          {
                            id: 'Load.EmailSendingWizard.DynamicallySelectSenderTooltipIntl'
                          },
                          {
                            linebreak: <br />,
                            strong: (chunk) => <strong>{chunk}</strong>
                          }
                        )}
                      </TooltipUtils>
                      <span data-tip data-for='customSenderTooltip'>
                        <StyledHelpIcon />
                      </span>
                    </Flex>
                  </GridCell>
                  {CustomSender && (
                    <>
                      <GridCell span={2}>
                        <MessageBox variant='info'>
                          {intl.formatMessage({
                            id: 'Load.EmailSendingWizard.DynamicSenderInfo'
                          })}
                        </MessageBox>
                      </GridCell>
                      <GridCell>
                        <DynamicSenderEmail />
                      </GridCell>

                      <GridCell>
                        <DynamicSenderName />
                      </GridCell>
                    </>
                  )}
                </>
              )}

              <GridCell>
                <InputSenderReplyTo />
              </GridCell>
            </Grid>
          </>
        )}
        {Detail.Action === ACTION_SEND_SMS && (
          <>
            <FormElement
              label={intl.formatMessage({
                id: 'Load.AutomationWorkflow.SenderSMS'
              })}
              required
              labelHelp={intl.formatMessage({
                id: 'Load.TrustedSmsSender.TooltipSmsSender'
              })}
            >
              <TrustedSmsInput
                {...trustedSmsSenderProps}
                onVerifyClick={handleVerify}
              />
              {trustedSmsBoxCodeVisible ? (
                <TrustedSmsBoxCode {...trustedSmsSenderCodeProps} />
              ) : null}
            </FormElement>
          </>
        )}
      </Panel>
    </>
  )
}
